import { useFrame, useThree } from '@react-three/fiber'
import { FC, useEffect, useMemo, useRef } from 'react'
import { Vector3 } from 'three'

/**
 * Component that animates camera to fly to a target position
 */
export const FlyToTarget: FC<{
  target: Vector3 | null
  onComplete: () => void
}> = ({ target, onComplete }) => {
  const { camera, controls } = useThree()
  const isFlying = useRef(false)
  const startPosition = useRef(new Vector3())
  const startTarget = useRef(new Vector3())
  const progress = useRef(0)

  // Reusable vectors for frame loop calculations
  const tempVectors = useMemo(
    () => ({
      offset: new Vector3(),
      endPosition: new Vector3(),
    }),
    []
  )

  useEffect(() => {
    if (target && !isFlying.current) {
      isFlying.current = true
      progress.current = 0
      startPosition.current.copy(camera.position)

      // Get current orbit target if available
      const orbitControls = controls as { target?: Vector3 }
      if (orbitControls?.target) {
        startTarget.current.copy(orbitControls.target)
      } else {
        startTarget.current.set(0, 0, 0)
      }
    }
  }, [target, camera, controls])

  useFrame((_, delta) => {
    if (!target || !isFlying.current) return

    progress.current += delta * 1.5 // Animation speed
    const t = Math.min(progress.current, 1)
    const eased = 1 - Math.pow(1 - t, 3) // Ease out cubic

    // Calculate camera offset from target - more top-down view
    const distance = 25
    tempVectors.offset.set(0, distance, distance * 0.3)

    // Interpolate camera position
    tempVectors.endPosition.copy(target).add(tempVectors.offset)
    camera.position.lerpVectors(
      startPosition.current,
      tempVectors.endPosition,
      eased
    )

    // Interpolate orbit target
    const orbitControls = controls as { target?: Vector3; update?: () => void }
    if (orbitControls?.target) {
      orbitControls.target.lerpVectors(startTarget.current, target, eased)
      orbitControls.update?.()
    }

    if (t >= 1) {
      isFlying.current = false
      onComplete()
    }
  })

  return null
}
